import {
    addMFA as addMfaService,
    getUserData,
    removeMFA as removeMFAService,
    resendPhoneVerificationCode as resendPhoneVerificationCodeService,
    updateUserAttributes,
    verifyPhone as verifyPhoneService,
    verifyTotpToken,
} from '/b2b/authentication/services';

import AmplifyActions from '/b2b/authentication/state/amplify/Amplify.actions';
import AuthActions from '/b2b/authentication/state/Auth.actions';
import { formatPhoneNumber } from '/b2b/common/helpers/Phone';

export const verifyPhone =
    ({ setAsPreferred, ...params }) =>
    dispatch => {
        dispatch(AuthActions.verifyPhoneBegin(params));

        return verifyPhoneService(params)
            .then(response => {
                dispatch(AuthActions.verifyPhoneSuccess(params, response));

                return response;
            })
            .catch(error => {
                const { message } = error;
                dispatch(AuthActions.verifyPhoneFailure(params, message));

                throw message;
            });
    };

export const addMFA = params => dispatch => {
    const { mfaType, setAsPreferred, verified } = params;
    dispatch(AuthActions.addMfaBegin(params));
    if (setAsPreferred) {
        dispatch(AuthActions.setPreferredMfaBegin(mfaType));
    }

    let mfaSetup = Promise.resolve();
    switch (mfaType) {
        case 'TOTP': {
            if (!verified) {
                mfaSetup = verifyTotpToken(params);
            }
            break;
        }
        case 'SMS': {
            if (!verified) {
                mfaSetup = verifyPhone(params)(dispatch);
            }
            break;
        }
    }

    return mfaSetup
        .then(() => addMfaService(mfaType, setAsPreferred))
        .then(mfaOptions => {
            if (setAsPreferred) {
                dispatch(AuthActions.setPreferredMfaSuccess(mfaType, mfaOptions));
            }
            dispatch(AuthActions.addMfaSuccess(params, mfaOptions));
            return mfaOptions;
        })
        .catch(error => {
            const { message } = error;
            if (setAsPreferred) {
                dispatch(AuthActions.setPreferredMfaFailure(mfaType, message));
            }
            dispatch(AuthActions.addMfaFailure(params, message));
            throw message;
        });
};

export const removeMFA =
    ({ mfaType = '' } = {}) =>
    dispatch => {
        return removeMFAService(mfaType)
            .then(mfaOptions => {
                dispatch(AuthActions.removeMfa(mfaType));
                const newMfas = Object.keys(mfaOptions)
                    .filter(value => mfaOptions[value] !== 'DISABLED')
                    .map(value => value.toUpperCase());
                if (newMfas.length === 1) {
                    return addMFA({ mfaType: newMfas[0], setAsPreferred: true, verified: true })(
                        dispatch
                    );
                }
                return mfaOptions;
            })
            .catch(error => {
                const { message } = error;
                throw message;
            });
    };

export const resendPhoneVerificationCode = () => dispatch => {
    return resendPhoneVerificationCodeService()
        .then(response => {
            dispatch(AmplifyActions.verifyPhone(response));
            return;
        })
        .catch(error => {
            const { message } = error;
            throw message;
        });
};

export const updatePhone =
    ({ phone }) =>
    dispatch => {
        const params = {
            phone: formatPhoneNumber(phone),
        };
        dispatch(AmplifyActions.updateUserAttributesBegin(params));

        return updateUserAttributes(params)
            .then(
                (/** @type {import('aws-amplify/auth').UpdateUserAttributesOutput} */ response) => {
                    if (
                        response.phone_number &&
                        response.phone_number.nextStep.updateAttributeStep === 'DONE'
                    ) {
                        // Sometimes the phone number needs to be re-verified, even when marked as DONE
                        return getUserData().then(userData => {
                            const { phone_number_verified: phoneNumberVerified } = userData;
                            if (!phoneNumberVerified || phoneNumberVerified === 'false') {
                                response.phone_number.nextStep.updateAttributeStep =
                                    'CONFIRM_ATTRIBUTE_WITH_CODE';
                                return resendPhoneVerificationCodeService().then(
                                    codeDeliveryDetails => {
                                        response.phone_number.nextStep.codeDeliveryDetails =
                                            codeDeliveryDetails;
                                        dispatch(
                                            AmplifyActions.updateUserAttributesSuccess(
                                                params,
                                                response
                                            )
                                        );
                                        return response;
                                    }
                                );
                            }
                            dispatch(AmplifyActions.updateUserAttributesSuccess(params, response));
                            return response;
                        });
                    }
                    dispatch(AmplifyActions.updateUserAttributesSuccess(params, response));
                    return response;
                }
            )
            .catch(error => {
                const { message } = error;
                dispatch(AmplifyActions.updateUserAttributesFailure(params, error));
                throw message;
            });
    };
